import { useQuery } from "@tanstack/react-query";
import { useEffect, useState, useCallback } from "react";

import { getEventsByOrderId, getOcurrences, getOrders } from "./api";
import { ordersStore } from "./store";
import { OrderGetDto, OcurrenceGetDto } from "./dto";

function useFetchOrders(params) {
  const { setOrders } = ordersStore();
  const [orders, _setOrders] = useState([]);

  const { data, isError, error, refetch, status, isFetching } = useQuery({
    queryKey: ["orders", params],
    queryFn: () => getOrders(params),
    enabled: !!params && (!!params.id_orden || (!!params.fechaInicioPedido && !!params.fechaFinPedido)),
  });

  useEffect(() => {
    if (data) {
      const mappedOrders = data.map((order) => new OrderGetDto(order));
      setOrders(mappedOrders);
      _setOrders(mappedOrders);
    }
  }, [data, setOrders]);

  return { data, isError, error, orders, refetch, status, isFetching };
}

function useFetchEventsByOrderId(query) {
  const { setEvents } = ordersStore();
  const [events, _setEvents] = useState([]);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["events"],
    queryFn: () => getEventsByOrderId(query),
    enabled: false,
  });

  useEffect(() => {
    if (data) {
      setEvents(data);
      _setEvents(data);
    }
  }, [data, setEvents]);

  return { data, isLoading, isError, error, events, refetch };
}

function useFetchOrdersById(orderId) {
  const stateOrder = ordersStore(
    useCallback(
      (state) => state.orders.find((s) => s.id === orderId),
      [orderId]
    )
  );

  const localStorageOrder =
    !!localStorage.getItem("CURRENT_ORDER") &&
    JSON.parse(localStorage.getItem("CURRENT_ORDER"));

  return stateOrder ? stateOrder : localStorageOrder;
}

function useFetchOrdersEditedById(id) {
  const localStorageOrders =
    !!localStorage.getItem("EDITED_ORDERS") &&
    JSON.parse(localStorage.getItem("EDITED_ORDERS"));

  return localStorageOrders && localStorageOrders.length > 0
    ? localStorageOrders.includes(id)
    : false;
}

function useStoreEditedOrders(orderId) {
  const localStorageOrders =
    !!localStorage.getItem("EDITED_ORDERS") &&
    JSON.parse(localStorage.getItem("EDITED_ORDERS"));

  const editedOrders =
    localStorageOrders && localStorageOrders.length > 0
      ? [...localStorageOrders, orderId]
      : [orderId];

  return localStorage.setItem(
    "EDITED_ORDERS",
    JSON.stringify([...editedOrders])
  );
}

function useFilteredOrders() {
  return ordersStore.getState().filteredOrders;
}

function useFilteredOcurrences() {
  return ordersStore.getState().filteredOcurrences;
}

function useFilters() {
  return {
    selectedFilters: ordersStore.getState().selectedFilters,
    addFilter: (filter) => ordersStore.getState().addSelectedFilter(filter),
    removeFilter: (filter) =>
      ordersStore.getState().removeSelectedFilter(filter),
    resetFilters: () => ordersStore.getState().resetFilters(),
  };
}

function useFetchOrdersBy(filters) {
  return ordersStore.getState().filterOrders(filters);
}

function useFetchOcurrencesBy(filters) {
  return ordersStore.getState().filterOcurrences(filters);
}

function useFetchOcurrences(params) {
  const { setOcurrences } = ordersStore();
  const [ocurrences, _setOcurrences] = useState([]);

  const { data, isError, error, refetch, status, isFetching } = useQuery({
    queryKey: ["ocurrences", params],
    queryFn: () => getOcurrences(params),
    enabled: !!params && (!!params.id_orden || (!!params.fechaInicioPedido && !!params.fechaFinPedido)),
  });

  useEffect(() => {
    if (data) {
      const mappedOcurrence = data.map(
        (ocurrence) => new OcurrenceGetDto(ocurrence)
      );
      setOcurrences(mappedOcurrence);
      _setOcurrences(mappedOcurrence);
    }
  }, [data, setOcurrences]);

  return { data, isError, error, ocurrences, refetch, status, isFetching };
}

export {
  useFetchOrders,
  useFetchOrdersById,
  useFilteredOrders,
  useFetchOrdersBy,
  useFetchEventsByOrderId,
  useFilters,
  useFetchOrdersEditedById,
  useStoreEditedOrders,
  useFetchOcurrences,
  useFetchOcurrencesBy,
  useFilteredOcurrences
};
